import { useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Clock, UserCheck } from "lucide-react";

const HOUR = 3600000;
export const waitInfo = (m) => {
  if (m.direction !== "in" || m.trashed || m.replied_at || m.handled_at) return null;
  const h = Math.max(0, (Date.now() - new Date(m.created_at).getTime()) / HOUR);
  const label = h < 1 ? `${Math.max(1, Math.round(h * 60))}m` : h < 48 ? `${Math.floor(h)}h` : `${Math.floor(h / 24)}d`;
  return { label, level: h >= 48 ? "red" : h >= 24 ? "amber" : "calm" };
};
const TONE = { red: "bg-red-50 text-red-700 border-red-200", amber: "bg-amber-50 text-amber-800 border-amber-200", calm: "bg-slate-50 text-slate-500 border-slate-200" };

export const WaitBadge = ({ m, testid }) => {
  const w = waitInfo(m);
  if (!w) return null;
  return (
    <span title="Waiting for a reply" data-testid={testid} data-level={w.level}
      className={`inline-flex items-center gap-1 text-[10px] font-bold border rounded-full px-1.5 py-0.5 ${TONE[w.level]}`}>
      <Clock size={10} /> {w.label}
    </span>
  );
};

export const AssignedBanner = ({ m }) => (m.assigned_to ? (
  <div className="mx-6 mb-3 rounded-xl border border-[#12234A]/15 bg-[#12234A]/[0.04] px-4 py-3 text-sm" data-testid="mail-assigned-banner">
    <p className="flex items-center gap-1.5 font-semibold text-[#12234A]"><UserCheck size={15} className="text-[#F5551E]" /> Assigned to {m.assigned_name} by {m.assigned_by}</p>
    {m.assign_note && <p className="mt-1 text-slate-600 whitespace-pre-line" data-testid="mail-assign-note">{m.assign_note}</p>}
  </div>
) : null);

export const AssignDialog = ({ m, onClose, onDone }) => {
  const [staff, setStaff] = useState([]);
  const [who, setWho] = useState(m.assigned_to || "");
  const [note, setNote] = useState(m.assign_note || "");
  useEffect(() => { api.get("/admin/mail/staff").then((r) => setStaff(r.data)).catch((e) => toast.error(errText(e))); }, []);
  const save = (user_id) => api.post(`/admin/mail/${m.id}/assign`, { user_id, note })
    .then(() => { toast.success(user_id ? "Email assigned" : "Assignment removed"); onDone(); }).catch((e) => toast.error(errText(e)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="mail-assign-dialog">
        <DialogHeader><DialogTitle>Assign this email</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={who} onValueChange={setWho}>
            <SelectTrigger data-testid="mail-assign-staff"><SelectValue placeholder="Choose a staff member" /></SelectTrigger>
            <SelectContent>{staff.map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.name || s.username} · {s.role}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={4} placeholder="Note for them (optional), e.g. 'Please reply about the refund'" data-testid="mail-assign-note-input" />
          <div className="flex justify-between gap-2">
            {m.assigned_to ? <Button variant="ghost" onClick={() => save("")} className="text-red-600" data-testid="mail-assign-remove">Remove assignment</Button> : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} data-testid="mail-assign-cancel">Cancel</Button>
              <Button onClick={() => save(who)} disabled={!who} className="bg-[#F5551E] hover:bg-[#e04a15]" data-testid="mail-assign-save">Assign</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
